import { useMemo } from 'react'
import { buildCrmMembership, buildUnifiedLeads } from '../data/unifiedLeads'
import type { CrmDataset } from '../types/crm'
import type { ProspectingDataset, ProspectingListId } from '../types/prospecting'
import type { UnifiedLead } from '../types/unifiedLead'

const PROSPECTING_LISTS: ProspectingListId[] = ['hot', 'audit', 'leads', 'old']

export type UnifiedLeadsState = {
  /** One record per unique prospect: CRM leads plus unmatched Prospecting leads. */
  leads: UnifiedLead[]
  /** Prospecting identity keys that exist in the Main CRM dataset. */
  crmMembership: Set<string>
}

/**
 * Joins the two workbooks for the main Leads page.
 *
 * Prospecting is strictly additive here: if it has not loaded, or failed to
 * load, the Leads page still shows the complete CRM dataset.
 */
export function useUnifiedLeads(
  crm: CrmDataset | null,
  prospecting: ProspectingDataset | null,
): UnifiedLeadsState {
  return useMemo(() => {
    const crmLeads = crm?.leads ?? []
    if (!prospecting) return { leads: buildUnifiedLeads(crmLeads, []), crmMembership: new Set<string>() }

    const everyProspect = PROSPECTING_LISTS.flatMap((list) => prospecting.lists[list])

    return {
      leads: buildUnifiedLeads(crmLeads, prospecting.lists.leads),
      // Membership is resolved for every tab, so a Hot or Audit row also knows
      // whether it already exists in the Main CRM.
      crmMembership: buildCrmMembership(crmLeads, everyProspect),
    }
  }, [crm, prospecting])
}
